import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './InvestorRelations.module.css';
import API_BASE_URL from '../../config/api';

const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const InvestorRelations = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [sections, setSections] = useState({});
  const [tabs, setTabs] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchSections = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(
          `${API_BASE_URL}/investor?lang=${lang}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        const list = Array.isArray(data.sections) ? data.sections : Array.isArray(data) ? data : [];

        const map = {};
        list.forEach((item) => {
          map[item.key] = {
            title: item.title,
            content: item.content || '',
            images: (item.images || []).map((img) => img.url || img.image_url || img.path).filter(Boolean),
            documents: (item.documents || []).map((doc) => ({
              name: doc.name,
              url: doc.file_url || doc.url,
            })),
          };
        });

        setSections(map);

        const newTabs = list.map((item) => ({ id: item.key, title: item.title }));
        setTabs(newTabs);
        setActiveTab((prev) =>
          prev && newTabs.some((tab) => tab.id === prev) ? prev : (newTabs[0]?.id ?? null),
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load investor sections', error);
        }
      }
    };

    fetchSections();

    return () => controller.abort();
  }, [i18n.language]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProjects = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(`${API_BASE_URL}/investor/projects?lang=${lang}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        setProjects(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load investor projects', error);
        }
      }
    };

    fetchProjects();

    return () => controller.abort();
  }, [i18n.language]);

  const currentTabData = sections[activeTab] || { title: '', content: '', images: [], documents: [] };
  const galleryImages = currentTabData.images || [];
  const documents = currentTabData.documents || [];
  const hasImages = galleryImages.length > 0;
  const hasDocuments = documents.length > 0;

  const formatContent = (text) => {
    if (!text) return null;
    if (text.includes('<')) {
      return <div dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return text.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return null;
      
      const isBullet = /^[•-]\s/.test(trimmedLine);
      const isListItem = /^\d+[.)]\s/.test(trimmedLine);
      
      if (isBullet) {
        const content = trimmedLine.replace(/^[•-]\s/, '');
        return (
          <div key={index} className={`${styles.listItem} ${styles.bulletItem}`}>
            {content}
          </div>
        );
      }
      
      if (isListItem) {
        return (
          <div key={index} className={`${styles.listItem} ${styles.numberedItem}`}>
            {trimmedLine}
          </div>
        );
      }
      return (
        <p key={index}>{trimmedLine}</p>
      );
    }).filter(Boolean);
  };

  return (
    <div className={styles.investorPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{t('investor.title')}</h1>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className={styles.tabsSection}>
        <div className={styles.container}>
          <div className={styles.tabsContainer}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedImage(0);
                  setIsGalleryOpen(false);
                }}
              >
                <span>{tab.title}</span>
                {activeTab === tab.id && <div className={styles.tabIndicator}></div>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          {activeTab === 'ourProjects' && projects.length > 0 ? (
            <div className={styles.projectsGrid}>
              {projects.map((project) => (
                <div key={project.slug || project.id} className={styles.projectCard}>
                  <div className={styles.projectImageWrap}>
                    {project.image_url ? (
                      <img
                        src={project.image_url}
                        alt={project.title}
                        className={styles.projectImage}
                      />
                    ) : (
                      <div className={styles.projectImagePlaceholder}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                    )}
                    <div className={styles.projectImageOverlay}></div>
                  </div>
                  <div className={styles.projectCardContent}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    <p className={styles.projectSummary}>
                      {stripHtml(project.summary || project.summary_en || project.summary_ar || '')}
                    </p>
                    <Link
                      to={`/investor-relations/project/${project.slug}`}
                      className={styles.projectKnowMoreBtn}
                    >
                      <span>{t('investor.ourProjects.learnMore', 'Learn More')}</span>
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : hasDocuments ? (
            <div>
              {documents.map((doc, index) => (
                <div key={index} className={styles.pdfSectionFullWidth} style={{ marginBottom: '2rem' }}>
                  <div className={styles.pdfContainer}>
                    <iframe
                      src={doc.url}
                      className={styles.pdfViewer}
                      title={doc.name || `Document ${index + 1}`}
                      type="application/pdf"
                    />
                    <div className={styles.pdfActions}>
                      <a
                        href={doc.url}
                        download
                        className={styles.downloadBtn}
                        aria-label={`Download ${doc.name}`}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <span>{doc.name || 'Download PDF'}</span>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`${styles.contentLayout} ${!hasImages ? styles.contentOnly : ''}`}>
              {/* Images Section */}
              {hasImages && (
                <div className={styles.imagesSection}>
                  <div className={styles.mainImageContainer}>
                    <div className={styles.mainImageWrapper}>
                      <img
                        src={galleryImages[selectedImage]}
                        alt={currentTabData.title}
                        className={styles.mainImage}
                        onClick={() => galleryImages.length > 1 && setIsGalleryOpen(true)}
                        style={galleryImages.length > 1 ? { cursor: 'pointer' } : {}}
                      />
                      {galleryImages.length > 1 && (
                        <button
                          className={styles.viewGalleryBtn}
                          onClick={() => setIsGalleryOpen(true)}
                          aria-label="View Gallery"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                          </svg>
                          <span>View All Images ({galleryImages.length})</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {galleryImages.length > 1 && (
                    <div className={styles.thumbnailGrid}>
                      {galleryImages.map((img, index) => (
                        <button
                          key={index}
                          className={`${styles.thumbnailItem} ${selectedImage === index ? styles.active : ''}`}
                          onClick={() => setSelectedImage(index)}
                          aria-label={`View image ${index + 1}`}
                        >
                          <img src={img} alt={`${currentTabData.title} ${index + 1}`} />
                          <div className={styles.thumbnailOverlay}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                              <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Content Section */}
              <div className={styles.textSection}>
                <div className={styles.contentCard}>
                  <h2 className={styles.contentTitle}>{currentTabData.title}</h2>
                  <div className={styles.content}>
                    {formatContent(currentTabData.content)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Full Screen Gallery Modal */}
      {isGalleryOpen && hasImages && galleryImages.length > 1 && (
        <div className={styles.galleryModal} onClick={() => setIsGalleryOpen(false)}>
          <button
            className={styles.closeGallery}
            onClick={() => setIsGalleryOpen(false)}
            aria-label="Close Gallery"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          
          <div className={styles.galleryContent} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.galleryNav}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1));
              }}
              aria-label="Previous Image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            
            <div className={styles.galleryImageContainer}>
              <img
                src={galleryImages[selectedImage]}
                alt={`${currentTabData.title} ${selectedImage + 1}`}
                className={styles.galleryMainImage}
              />
            </div>
            
            <button
              className={styles.galleryNav}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0));
              }}
              aria-label="Next Image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <div className={styles.galleryFooter} onClick={(e) => e.stopPropagation()}>
            <div className={styles.galleryCounter}>
              {selectedImage + 1} / {galleryImages.length}
            </div>
            <div className={styles.galleryThumbnails}>
              {galleryImages.map((img, index) => (
                <button
                  key={index}
                  className={`${styles.galleryThumbnail} ${selectedImage === index ? styles.active : ''}`}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Go to image ${index + 1}`}
                >
                  <img src={img} alt={`${currentTabData.title} ${index + 1}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorRelations;
